"use client";

import { Stethoscope, Wind, Activity, Moon, Zap, ClipboardCheck, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const referralReasons = [{
  title: "Pulmonary Consultation",
  description: "For adults with respiratory symptoms, abnormal imaging, complex pulmonary history, or concerns requiring specialist evaluation.",
  icon: Stethoscope
}, {
  title: "COPD & Asthma Care",
  description: "For patients with COPD, asthma, wheezing, recurrent flare-ups, medication concerns, or ongoing breathing difficulty.",
  icon: Wind
}, {
  title: "Chronic Cough & Shortness of Breath",
  description: "For persistent cough, unexplained shortness of breath, abnormal breathing symptoms, or symptoms not improving with initial treatment.",
  icon: Activity
}, {
  title: "Sleep Apnea Evaluation & CPAP Support",
  description: "For suspected sleep apnea, snoring, daytime fatigue, CPAP intolerance, mask issues, or ongoing sleep-related breathing concerns.",
  icon: Moon
}, {
  title: "Oxygen Therapy Management",
  description: "For patients using oxygen therapy or needing evaluation, monitoring, or follow-up related to oxygen requirements.",
  icon: Zap
}, {
  title: "Post-Hospital Pulmonary Follow-Up",
  description: "For patients recently hospitalized with pneumonia, COPD flare-ups, respiratory illness, oxygen needs, or other pulmonary concerns.",
  icon: ClipboardCheck
}];
export default function HowToSendAReferral() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3386" id="how-to-send-a-referral" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32 lg:py-40 px-6 sm:px-10 lg:px-16">
      <div className="mx-auto max-w-7xl">
        <div ref={headerReveal.ref} className={["mb-16 transition-all duration-[900ms] ease-out", headerReveal.isVisible ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-6 blur-sm"].join(" ")}>
          <span className="font-mono text-xs uppercase tracking-[0.22em]" style={{
          color: "var(--chart-4)"
        }}>
            Clinical Indications
          </span>
          <h2 className="font-default font-bold mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-foreground">
            Common Reasons for Referral
          </h2>
          <div className="mt-8 h-px w-24 bg-border" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {referralReasons.map((reason, i) => {
          const Icon = reason.icon;
          return <div key={i} data-index={i} className="group relative rounded-lg border border-border bg-card p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-accent/30" style={{
              color: "var(--chart-2)"
            }}>
                  <Icon strokeWidth={1.5} className="h-6 w-6" />
                </div>
                
                <h3 className="font-default font-semibold text-xl leading-tight tracking-tight text-card-foreground">
                  {reason.title}
                </h3>
                
                <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground font-default">
                  {reason.description}
                </p>

                <div className="mt-8 pt-6 border-t border-border/60">
                  <Link to="/ContactPage" className="inline-flex items-center gap-2 text-sm font-medium text-chart-2 opacity-70 transition-opacity group-hover:opacity-100">
                    <span>Refer a patient</span>
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                  </Link>
                </div>
              </div>;
        })}
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild variant="default" size="lg" className="rounded-lg">
            <Link to="/ContactPage">
              Fax Referral (412) 533-5622
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="rounded-lg">
            <Link to="tel:+14126729240">
              Call Office (412) 672-9240
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}